import { useState, useEffect } from 'react';

const Q_CATEGORIES = [
  'Investment Objectives',
  'Investment Experience',
  'Risk Tolerance',
  'Time Horizon',
  'Financial Situation',
  'Behavioral',
];

function makeQUid() {
  return 'q_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

function makeOptUid() {
  return 'opt_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4);
}

export default function RiskProfileEditor({ questions, scoreRanges, stages, onSave, saving }) {
  const [localQuestions, setLocalQuestions] = useState([]);
  const [localRanges, setLocalRanges] = useState([]);
  const [view, setView] = useState('questions');
  const [expandedUid, setExpandedUid] = useState(null);

  useEffect(() => {
    setLocalQuestions(questions || []);
  }, [questions]);

  useEffect(() => {
    setLocalRanges(scoreRanges || []);
  }, [scoreRanges]);

  function handleQChange(uid, key, value) {
    setLocalQuestions(prev => prev.map(q => q.uid === uid ? { ...q, [key]: value } : q));
  }

  function handleOptChange(qUid, optUid, key, value) {
    setLocalQuestions(prev => prev.map(q => {
      if (q.uid !== qUid) return q;
      const opts = q.options.map(o => o.uid === optUid ? { ...o, [key]: value } : o);
      return { ...q, options: opts };
    }));
  }

  function handleAddOption(qUid) {
    setLocalQuestions(prev => prev.map(q => {
      if (q.uid !== qUid) return q;
      const newOpt = { uid: makeOptUid(), text: 'New option', score: 1 };
      return { ...q, options: [...(q.options || []), newOpt] };
    }));
  }

  function handleDeleteOption(qUid, optUid) {
    setLocalQuestions(prev => prev.map(q => {
      if (q.uid !== qUid) return q;
      return { ...q, options: q.options.filter(o => o.uid !== optUid) };
    }));
  }

  function handleAddQuestion() {
    const newQ = {
      uid: makeQUid(),
      question_text: 'New question',
      category: Q_CATEGORIES[0],
      question_type: 'single_choice',
      options: [],
      stage_uid: '',
      order: localQuestions.length + 1,
    };
    setLocalQuestions(prev => [...prev, newQ]);
    setExpandedUid(newQ.uid);
  }

  function handleDeleteQuestion(uid) {
    if (!window.confirm('Delete this question?')) return;
    setLocalQuestions(prev => prev.filter(q => q.uid !== uid));
    if (expandedUid === uid) setExpandedUid(null);
  }

  function handleRangeChange(idx, key, value) {
    setLocalRanges(prev => prev.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  }

  function handleToggleExpand(uid) {
    setExpandedUid(prev => prev === uid ? null : uid);
  }

  function handleSave() {
    onSave(localQuestions, localRanges);
  }

  const sorted = [...localQuestions].sort((a, b) => a.order - b.order);

  return (
    <div>
      <div className="pm__mini-tabs">
        <button
          className={`pm__mini-tab ${view === 'questions' ? 'pm__mini-tab--active' : ''}`}
          onClick={() => setView('questions')}
        >
          Questions ({localQuestions.length})
        </button>
        <button
          className={`pm__mini-tab ${view === 'ranges' ? 'pm__mini-tab--active' : ''}`}
          onClick={() => setView('ranges')}
        >
          Score Bands
        </button>
      </div>

      {view === 'questions' && (
        <>
          <div className="pm__toolbar">
            <span className="pm__section-title">Risk Assessment Questions</span>
            <button className="btn btn--outline-primary" onClick={handleAddQuestion}>+ Add Question</button>
          </div>

          <div className="pm__q-list">
            {sorted.map((q, idx) => (
              <div key={q.uid} className="pm__q-card">
                <div className="pm__q-header" onClick={() => handleToggleExpand(q.uid)}>
                  <span className="pm__q-num">{idx + 1}.</span>
                  <span className="pm__q-text">{q.question_text}</span>
                  <span className="pm__q-cat">{q.category}</span>
                  <span style={{ marginLeft: '8px', color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                    {expandedUid === q.uid ? '▲' : '▼'}
                  </span>
                </div>

                {expandedUid === q.uid && (
                  <div className="pm__q-body">
                    <textarea
                      className="pm__q-text-input"
                      value={q.question_text}
                      onChange={e => handleQChange(q.uid, 'question_text', e.target.value)}
                      placeholder="Question text"
                    />
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
                      <select
                        className="pm__select"
                        value={q.category}
                        onChange={e => handleQChange(q.uid, 'category', e.target.value)}
                      >
                        {Q_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select
                        className="pm__select"
                        value={q.question_type}
                        onChange={e => handleQChange(q.uid, 'question_type', e.target.value)}
                      >
                        <option value="single_choice">Single Choice</option>
                        <option value="multi_choice">Multi Choice</option>
                      </select>
                      <select
                        className="pm__select"
                        style={{ minWidth: '130px' }}
                        value={q.stage_uid || ''}
                        onChange={e => handleQChange(q.uid, 'stage_uid', e.target.value)}
                      >
                        <option value="">No stage</option>
                        {(stages || []).map(s => (
                          <option key={s.uid} value={s.uid}>{s.icon} {s.name}</option>
                        ))}
                      </select>
                      <button className="pm__del-btn" onClick={() => handleDeleteQuestion(q.uid)} title="Delete question">
                        &#10005;
                      </button>
                    </div>

                    <div className="pm__opt-list">
                      {(q.options || []).map(opt => (
                        <div key={opt.uid} className="pm__opt-row">
                          <input
                            className="pm__input"
                            style={{ flex: 1 }}
                            value={opt.text}
                            onChange={e => handleOptChange(q.uid, opt.uid, 'text', e.target.value)}
                            placeholder="Option text"
                          />
                          <input
                            type="number"
                            className="pm__input"
                            style={{ width: '60px' }}
                            value={opt.score}
                            onChange={e => handleOptChange(q.uid, opt.uid, 'score', parseInt(e.target.value) || 0)}
                            min="0"
                            max="20"
                            title="Score (0-20)"
                          />
                          <button className="pm__del-btn" onClick={() => handleDeleteOption(q.uid, opt.uid)}>
                            &#10005;
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="btn btn--outline"
                      style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                      onClick={() => handleAddOption(q.uid)}
                    >
                      + Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}
            {localQuestions.length === 0 && (
              <div className="pm__empty">No questions yet. Click + Add Question to create one.</div>
            )}
          </div>
        </>
      )}

      {view === 'ranges' && (
        <>
          <div className="pm__toolbar">
            <span className="pm__section-title">Score Band Configuration</span>
          </div>
          <table className="pm__score-table">
            <thead>
              <tr>
                <th>Min Score</th>
                <th>Max Score</th>
                <th>Risk Label</th>
                <th>Color</th>
                <th>Preview</th>
              </tr>
            </thead>
            <tbody>
              {localRanges.map((r, idx) => (
                <tr key={idx}>
                  <td>
                    <input
                      type="number"
                      className="pm__input"
                      style={{ width: '60px' }}
                      value={r.min}
                      onChange={e => handleRangeChange(idx, 'min', parseInt(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="pm__input"
                      style={{ width: '60px' }}
                      value={r.max}
                      onChange={e => handleRangeChange(idx, 'max', parseInt(e.target.value) || 0)}
                    />
                  </td>
                  <td>
                    <input
                      className="pm__input"
                      style={{ width: '160px' }}
                      value={r.label}
                      onChange={e => handleRangeChange(idx, 'label', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="color"
                      value={r.color}
                      onChange={e => handleRangeChange(idx, 'color', e.target.value)}
                      style={{ width: '40px', height: '28px', padding: '2px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }}
                    />
                  </td>
                  <td>
                    <span style={{ background: r.color + '22', color: r.color, border: `1px solid ${r.color}`, padding: '2px 10px', borderRadius: 'var(--radius-pill)', fontSize: '0.6875rem', fontWeight: '700' }}>
                      {r.label}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="pm__editor-footer">
        <button
          className={`btn ${saving ? 'btn--disabled' : 'btn--primary'}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
